// src/views/pages/BannedBlockedScripts.js
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getBannedBlockedScriptList } from "Store/Reducer/bannedblockedscript.slice";
import { getScriptSettingList } from "Store/Reducer/settingscript.slice";
import { getSessionData } from "utils/common";
import { formatUiDate } from "utils/dateFormats";
import { errorLog } from "utils/logger";
import useMobileViewport from "hooks/useMobileViewport";
import MobileColumnList from "components/mobile/MobileColumnList";

const EMPTY_ARRAY = [];

function SortIcon({ direction }) {
  if (!direction)
    return (
      <span className="bbs-sort-icon bbs-sort-icon--idle">
        <span>▲</span>
        <span>▼</span>
      </span>
    );
  return (
    <span className="bbs-sort-icon bbs-sort-icon--active">
      {direction === "asc" ? "▲" : "▼"}
    </span>
  );
}

function ScriptTable({ columns, data, loading, emptyText }) {
  const isMobileViewport = useMobileViewport();
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const sorted = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return EMPTY_ARRAY;
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [data, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  if (isMobileViewport) {
    const subtitleKey = columns.find((col) => col.key === "segmentName" || col.key === "segment")?.key;
    return (
      <MobileColumnList
        columns={columns}
        data={sorted}
        loading={loading}
        emptyText={emptyText || "No data found"}
        titleKey="symbol"
        subtitleKey={subtitleKey}
      />
    );
  }

  return (
    <div className="bbs-table-scroll">
      <table className="bbs-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                data-bbs-col={col.key}
                onClick={() => !col.noSort && handleSort(col.key)}
                className={col.noSort ? "bbs-th--nosort" : "bbs-th--sortable"}
              >
                {col.label}
                {!col.noSort && <SortIcon direction={sortKey === col.key ? sortDir : null} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="bbs-empty bbs-empty--loading">
                ⏳ Loading…
              </td>
            </tr>
          ) : sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <div className="bbs-empty">
                  <div className="bbs-empty__icon">📭</div>
                  <div className="bbs-empty__text">{emptyText || "No data found"}</div>
                </div>
              </td>
            </tr>
          ) : (
            sorted.map((row, i) => (
              <tr key={row.id || i} className={i % 2 === 0 ? "bbs-row--even" : "bbs-row--odd"}>
                {columns.map(col => (
                  <td
                    key={col.key}
                    data-bbs-col={col.key}
                    className={col.key === "symbol" ? "bbs-td--symbol" : undefined}
                  >
                    {col.render
                      ? col.render(row)
                      : row[col.key] != null
                        ? String(row[col.key])
                        : <span className="bbs-muted">—</span>
                    }
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}



export default function BannedBlockedScripts({ darkTheme }) {
  const dispatch = useDispatch();
  const session = getSessionData();
  const userId = String(session?.userId ?? session?.id ?? session?.UserId ?? "").trim();

  const [scriptType,     setScriptType]     = useState("bannedScript");
  const [blockedFetched, setBlockedFetched] = useState(false);

  const bannedList    = useSelector(state => state.bannedBlockedScript?.bannedBlockedScriptList    || EMPTY_ARRAY);
  const bannedLoading = useSelector(state => state.bannedBlockedScript?.bannedBlockedScriptLoading ?? false);
  const blockedList   = useSelector(state => state.settingScript?.scriptSettingList                || EMPTY_ARRAY);
  const blockedLoading= useSelector(state => state.settingScript?.ScriptLoading                   ?? false);

  useEffect(() => {
    dispatch(getBannedBlockedScriptList());
  }, [dispatch]);

  useEffect(() => {
    if (scriptType !== "blockedScript") return;
    if (blockedFetched) return;
    if (!userId) { errorLog("UserId missing"); return; }
    setBlockedFetched(true);
    dispatch(getScriptSettingList({
      userId,
      pageNo    : 1,
      pageSize  : 10000,
      searchText: "",
      appendData: false,
    }));
  }, [scriptType, userId, blockedFetched, dispatch]);

  const isBanned = scriptType === "bannedScript";
  const data     = isBanned ? bannedList    : blockedList;
  const loading  = isBanned ? bannedLoading : blockedLoading;

  const bannedColumns = useMemo(() => [
    { key:"segmentName", label:"Script" },
    { key:"symbol",      label:"Symbol" },
    {
      key   : "startDate",
      label : "From Date",
      render: row => {
        const d = formatUiDate(row.startDate || row.addTime || row.createdAt, null);
        return d
          ? <span>{d}</span>
          : <span className="bbs-muted">—</span>;
      },
    },
    {
      key   : "endDate",
      label : "To Date",
      render: row => {
        const d = formatUiDate(row.endDate || row.addTime || row.createdAt, null);
        return d
          ? <span>{d}</span>
          : <span className="bbs-muted">—</span>;
      },
    },
    {
      key   : "createdBy",
      label : "Block By",
      render: row => row.createdBy
        ? <span>{row.createdBy}</span>
        : <span className="bbs-muted">-</span>,
    },
  ], []);

  const blockedColumns = useMemo(() => [
    { key:"segment", label:"Script" },
    { key:"symbol",  label:"Symbol" },
    {
      key   : "addTime",
      label : "From Date",
      render: row => {
        const d = formatUiDate(row.addTime, null);
        return d
          ? <span>{d}</span>
          : <span className="bbs-muted">—</span>;
      },
    },
    {
      key   : "addTime_to",
      label : "To Date",
      noSort: true,
      render: row => {
        const d = formatUiDate(row.addTime, null);
        return d
          ? <span>{d}</span>
          : <span className="bbs-muted">—</span>;
      },
    },
    {
      key   : "blockedBy",
      label : "Block By",
      render: row => {
        const name = row.blockedBy || row.createdByName || row.createdBy || row.blockedByName || null;
        return name
          ? <span>{name}</span>
          : <span className="bbs-muted">-</span>;
      },
    },
  ], []);

  const columns      = isBanned ? bannedColumns  : blockedColumns;
  const bannedCount  = bannedList.length;
  const blockedCount = blockedList.length;
  const isMobileViewport = useMobileViewport();

  return (
    <div className={`bbs-page${isMobileViewport ? " fx-module--mobile-cards" : ""}`}>

      <div className="bbs-title-row">
        <span className="bbs-title">
          🚫 Banned / Blocked Scripts
        </span>
        <span className="bbs-count">
          {data.length} record{data.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="bbs-tabs">
        {[
          { value:"bannedScript",  label:"🚫 Banned Script",  count:bannedCount  },
          { value:"blockedScript", label:"🔒 Blocked Script", count:blockedCount },
        ].map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setScriptType(opt.value)}
            className={`bbs-tab${scriptType === opt.value ? " bbs-tab--active" : ""}`}
          >
            {opt.label}
            <span className="bbs-tab-badge">
              {opt.count}
            </span>
          </button>
        ))}
      </div>

      <ScriptTable
        columns={columns}
        data={data}
        loading={loading}
        emptyText={`No ${isBanned ? "banned" : "blocked"} scripts found`}
      />

    </div>
  );
}
